import { isWithinCzechia } from './bounds.js';
import { parseCoordinateInput } from './coordinates.js';
import { sjtskToWgs84 } from './crs.js';
/** Hosti, u kterych odkaz znam. Bez wildcardu — `endsWith` by pustil `evil-mapy.cz`. */
const PROVIDER_HOSTS = [
    { provider: 'mapy', hosts: ['mapy.cz', 'www.mapy.cz', 'mapy.com', 'www.mapy.com'] },
    {
        provider: 'google',
        hosts: [
            'google.com',
            'www.google.com',
            'maps.google.com',
            'google.cz',
            'www.google.cz',
            'goo.gl',
            'maps.app.goo.gl',
        ],
    },
    { provider: 'osm', hosts: ['openstreetmap.org', 'www.openstreetmap.org'] },
];
/** Kratke odkazy — host, ktery NESLOUZI souradnice, ale jen presmerovani. */
const SHORT_LINK_HOSTS = {
    mapy: [],
    google: ['goo.gl', 'www.goo.gl', 'maps.app.goo.gl'],
    osm: [],
};
/** Vsichni hosti, ktere parser zna — taky pro `resolveShortMapLink` jako allowlist. */
export const MAP_LINK_HOSTS = PROVIDER_HOSTS.flatMap((entry) => entry.hosts);
/** Kratky odkaz u mapy ma p cestu `/s/<token>` (Mapy.cz) nebo `/shortlink/<token>`. */
const SHORT_PATH_PREFIXES = {
    mapy: ['/s/', '/shortlink/'],
    google: [],
    osm: [],
};
function hostOf(url) {
    return url.hostname.toLowerCase();
}
function providerOf(host) {
    for (const entry of PROVIDER_HOSTS) {
        if (entry.hosts.includes(host))
            return entry.provider;
    }
    return null;
}
/** Kratky odkaz: bud host je kratkylinkovy, nebo cesta hosta map zacina `/s/`. */
function isShortLink(provider, url) {
    if (SHORT_LINK_HOSTS[provider].includes(hostOf(url)))
        return true;
    const pathname = url.pathname;
    return SHORT_PATH_PREFIXES[provider].some((prefix) => pathname.startsWith(prefix));
}
/** `?x=&y=` z mapy: S-JTSK nebo WGS84 podle velikosti (brief GPS1). */
function mapyXY(xRaw, yRaw) {
    const x = Number(xRaw);
    const y = Number(yRaw);
    if (!Number.isFinite(x) || !Number.isFinite(y))
        return null;
    // Letecka mappa Mapy.cz nese S-JTSK (obe záporné, stisíce), zakladni a
    // turisticka WGS84 (sirka 48-51, delka 12-19). Rozsah je oddeli bez
    // mrakovani na zakladni.
    if (x < 0 && y < 0)
        return sjtskToWgs84({ x, y });
    return { lat: y, lon: x };
}
function parseMapy(url) {
    const source = url.searchParams.get('source');
    const id = url.searchParams.get('id');
    if (source !== null && source.toLowerCase() === 'coor' && id !== null) {
        // `id=15.1763103,50.2003398` — poradi LON, LAT (jinak nez Mapy `x`/`y`).
        const parts = id.split(',');
        if (parts.length !== 2)
            return { kind: 'error', code: 'malformed' };
        const lon = Number((parts[0] ?? '').trim());
        const lat = Number((parts[1] ?? '').trim());
        if (!Number.isFinite(lat) || !Number.isFinite(lon))
            return { kind: 'error', code: 'malformed' };
        return result({ lat, lon }, 'mapy');
    }
    const x = url.searchParams.get('x');
    const y = url.searchParams.get('y');
    if (x === null || y === null)
        return { kind: 'error', code: 'malformed' };
    const point = mapyXY(x, y);
    if (point === null)
        return { kind: 'error', code: 'malformed' };
    return result(point, 'mapy');
}
function parseGoogle(url) {
    // `/maps/@<lat>,<lon>,<zoom>z` — nejcastejsi tvar z tlacitka „Sdilet".
    const at = /@(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/u.exec(url.pathname);
    if (at !== null) {
        const lat = Number(at[1]);
        const lon = Number(at[2]);
        if (!Number.isFinite(lat) || !Number.isFinite(lon))
            return { kind: 'error', code: 'malformed' };
        return result({ lat, lon }, 'google');
    }
    // `?q=<lat>,<lon>` i `?ll=<lat>,<lon>` — POZOR: `q` casto neni souradnice
    // ale nazev mista („Praha, Hašlerova 1"). Bereme ho jen kdyz je ciselna
    // dvojice; jinak to neni souradnice a nechame vyhledavani adres.
    for (const key of ['q', 'query', 'll', 'center']) {
        const value = url.searchParams.get(key);
        if (value === null)
            continue;
        const parts = value.split(',');
        if (parts.length !== 2)
            continue;
        const lat = Number((parts[0] ?? '').trim());
        const lon = Number((parts[1] ?? '').trim());
        if (!Number.isFinite(lat) || !Number.isFinite(lon))
            continue;
        return result({ lat, lon }, 'google');
    }
    return { kind: 'error', code: 'malformed' };
}
function parseOsm(url) {
    // `#map=<z>/<lat>/<lon>` — hash, ne cesta; proto `url.hash`.
    const map = /#map=(-?\d+(?:\.\d+)?)\/(-?\d+(?:\.\d+)?)\/(-?\d+(?:\.\d+)?)/u.exec(url.hash);
    if (map === null)
        return { kind: 'error', code: 'malformed' };
    const lat = Number(map[2]);
    const lon = Number(map[3]);
    if (!Number.isFinite(lat) || !Number.isFinite(lon))
        return { kind: 'error', code: 'malformed' };
    return result({ lat, lon }, 'osm');
}
function result(point, provider) {
    if (!isWithinCzechia(point))
        return { kind: 'error', code: 'outsideCzechia' };
    return { kind: 'point', point, provider };
}
/**
 * Odkaz z mapove aplikace → bod.
 *
 * `kind: 'none'` znamená, že to URL vůbec není (např. „Umyslovice 38/13" —
 * to není odkaz, to je adresa). `kind: 'error'` znamená, že odkaz JE, ale
 * souřadnici z něj nevytáhneme (chybí `x`/`y`, `@` je text místo čísla…).
 */
export function parseMapLink(raw) {
    const text = raw.trim();
    if (text === '')
        return { kind: 'none' };
    let url;
    try {
        url = new URL(text);
    }
    catch {
        return { kind: 'none' };
    }
    if (url.protocol !== 'http:' && url.protocol !== 'https:')
        return { kind: 'none' };
    const provider = providerOf(hostOf(url));
    if (provider === null)
        return { kind: 'none' };
    if (isShortLink(provider, url)) {
        return { kind: 'short', link: { url: url.toString(), provider } };
    }
    if (provider === 'mapy')
        return parseMapy(url);
    if (provider === 'google')
        return parseGoogle(url);
    return parseOsm(url);
}
/**
 * Odkaz z mapove aplikace rozbaleny az za serverem. Projde `parseMapLink`
 * znovu — kratky odkaz po presmerovani ukazuje na plnou mapovou adresu.
 */
export function parseExpandedMapLink(url) {
    const parsed = parseMapLink(url);
    // Kratky odkaz, ktery se po rozbaleni nezměnil, znovu nezkousime donekonecna.
    return parsed.kind === 'short' ? { kind: 'error', code: 'malformed' } : parsed;
}
export function parseLocationInput(raw) {
    const link = parseMapLink(raw);
    if (link.kind !== 'none')
        return link;
    return parseCoordinateInput(raw);
}
//# sourceMappingURL=map-link.js.map